/**
 * Battle March mode for the General's Adjutant.
 *
 * Battle March replaces the Matched Play scenarios with its own deployment
 * maps, objectives and Victory Point scheme, so it is a separate step rather
 * than another scenario in the list.
 */
import { useState } from 'react';
import {
  BATTLEFIELD, DEPLOYMENT_MAPS, DEPLOYMENT_RULES, OBJECTIVES_TABLE, OBJECTIVE_RULES,
  RANDOM_HAPPENINGS, RANDOM_HAPPENINGS_USAGE, SECONDARY_OBJECTIVES, UNUSUAL_PROPERTIES_TABLE,
  VICTORY_POINTS, type D6TableEntry, type DeploymentMap,
} from '../../data/battleMarch';

const FIELD = 'rgba(120,110,90,0.10)';
const EDGE = 'rgba(120,110,90,0.55)';
const ZONE_A = 'rgba(180,140,80,0.55)';
const ZONE_B = 'rgba(150,160,180,0.50)';
const DASH = 'rgba(90,85,70,0.75)';

/**
 * The six Battle March maps, drawn on the minimum 44" x 30" battlefield at
 * 4px per inch. Every dimension here is one printed on the book's diagrams.
 */
function BattleMarchMap({ id, mirrored }: { id: string; mirrored: boolean }) {
  const w = 176; const h = 120; const IN = 4;
  const zoneA = { fill: ZONE_A, stroke: EDGE, strokeWidth: 1 };
  const zoneB = { fill: ZONE_B, stroke: EDGE, strokeWidth: 1 };
  const lbl = (x: number, y: number, text: string, sz = 7) => (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="middle"
      stroke="rgba(255,255,255,0.95)" strokeWidth="3" strokeLinejoin="round" paintOrder="stroke"
      style={{ fontSize: `${sz}px`, fill: '#1a1a2e', fontFamily: 'Cinzel,serif', fontWeight: 700 }}>
      {text}
    </text>
  );

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', maxWidth: '340px', display: 'block', margin: '0 auto' }}
      aria-label={`${id} deployment map`}>
      <g transform={mirrored ? `translate(${w},0) scale(-1,1)` : undefined}>
        <rect x={0} y={0} width={w} height={h} fill={FIELD} stroke={EDGE} strokeWidth={1.5} />

        {id === 'pitched_battle' && <>
          <rect x={0} y={0} width={w} height={7.5 * IN} {...zoneA} />
          <rect x={0} y={h - 7.5 * IN} width={w} height={7.5 * IN} {...zoneB} />
          <line x1={4} y1={h / 2} x2={w - 4} y2={h / 2} stroke={DASH} strokeWidth={1} strokeDasharray="6,4" />
          {lbl(w / 2, 15, 'ZONE A')}{lbl(w / 2, h - 15, 'ZONE B')}
          {lbl(w / 2, 45, '7.5"', 6)}{lbl(w / 2, 75, '7.5"', 6)}
        </>}

        {id === 'close_encounter' && <>
          <rect x={0} y={0} width={w / 2} height={h / 2} {...zoneA} />
          <rect x={w / 2} y={h / 2} width={w / 2} height={h / 2} {...zoneB} />
          <circle cx={w / 2} cy={h / 2} r={7.5 * IN} fill={FIELD} stroke={EDGE} strokeWidth={1.2} />
          {lbl(w / 4, 22, 'ZONE A')}{lbl(w * 0.75, h - 22, 'ZONE B')}
          {lbl(w / 2, h / 2, '15"', 6)}
        </>}

        {id === 'opposed_flanks' && <>
          <polygon points={`0,0 ${w},0 ${w},${18 * IN}`} {...zoneA} />
          <polygon points={`0,${h - 18 * IN} 0,${h} ${w},${h}`} {...zoneB} />
          {lbl(w * 0.72, 22, 'ZONE A')}{lbl(w * 0.28, h - 22, 'ZONE B')}
          {lbl(w - 14, 40, '18"', 6)}{lbl(14, h - 40, '18"', 6)}
        </>}

        {id === 'meeting_engagement' && <>
          <rect x={0} y={0} width={w - 11 * IN} height={7.5 * IN} {...zoneA} />
          <rect x={11 * IN} y={h - 7.5 * IN} width={w - 11 * IN} height={7.5 * IN} {...zoneB} />
          <line x1={4} y1={h / 2} x2={w - 4} y2={h / 2} stroke={DASH} strokeWidth={1} strokeDasharray="6,4" />
          {lbl((w - 11 * IN) / 2, 15, 'ZONE A')}{lbl(11 * IN + (w - 11 * IN) / 2, h - 15, 'ZONE B')}
          {lbl(w / 2, 45, '7.5"', 6)}{lbl(w / 2, 75, '7.5"', 6)}
          {lbl(w - 22, 8, '11"', 6)}{lbl(22, h - 8, '11"', 6)}
        </>}

        {id === 'mountain_pass' && <>
          <rect x={0} y={0} width={11 * IN} height={h} {...zoneA} />
          <rect x={w - 11 * IN} y={0} width={11 * IN} height={h} {...zoneB} />
          <line x1={w / 2} y1={4} x2={w / 2} y2={h - 4} stroke={DASH} strokeWidth={1} strokeDasharray="6,4" />
          {lbl(11 * IN / 2, h / 2, 'A')}{lbl(w - 11 * IN / 2, h / 2, 'B')}
          {lbl(w * 0.36, h / 2, '11"', 6)}{lbl(w * 0.64, h / 2, '11"', 6)}
        </>}

        {id === 'outflank' && <>
          <polygon points={`0,0 ${22 * IN},0 0,${h}`} {...zoneA} />
          <polygon points={`${w},0 ${w},${h} ${w - 22 * IN},${h}`} {...zoneB} />
          {lbl(24, 24, 'ZONE A')}{lbl(w - 24, h - 24, 'ZONE B')}
          {lbl(w * 0.68, 8, '22"', 6)}{lbl(w * 0.32, h - 8, '22"', 6)}
        </>}
      </g>
    </svg>
  );
}

function rollD6() { return Math.ceil(Math.random() * 6); }
function resultFor(table: D6TableEntry[], roll: number) {
  return table.find((r) => r.roll.includes(roll));
}

/** A D6 table with a roll button and the full table underneath */
function RollTable({ title, note, table, onRoll }: {
  title: string; note?: string; table: D6TableEntry[]; onRoll?: (r: number) => void;
}) {
  const [roll, setRoll] = useState<number | null>(null);
  const result = roll ? resultFor(table, roll) : null;
  return (
    <div className="rounded border p-3 mb-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h4 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h4>
        <button
          onClick={() => { const r = rollD6(); setRoll(r); onRoll?.(r); }}
          className="px-3 py-1 rounded text-sm font-semibold"
          style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', color: 'var(--color-accent-amber)' }}
        >⚄ Roll</button>
      </div>
      {note && <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>{note}</p>}
      {result && (
        <p className="text-sm mb-2" style={{ color: 'var(--color-accent-amber)' }}>
          Rolled <strong>{roll}</strong> — <strong>{result.name}</strong>
        </p>
      )}
      <ul className="text-xs space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
        {table.map((r) => (
          <li key={r.name} style={{ opacity: result && result.name !== r.name ? 0.5 : 1 }}>
            <strong>{r.roll.length > 1 ? `${r.roll[0]}-${r.roll[r.roll.length - 1]}` : r.roll[0]}</strong>{' '}
            <strong>{r.name}:</strong> {r.effect}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BattleMarchSetup() {
  const [mapId, setMapId] = useState<string | null>(null);
  const [mirrored, setMirrored] = useState(false);
  const [happeningsId, setHappeningsId] = useState<string>(RANDOM_HAPPENINGS[0].id);
  const selectedMap: DeploymentMap | undefined = DEPLOYMENT_MAPS.find((m) => m.id === mapId);
  const happenings = RANDOM_HAPPENINGS.find((t) => t.id === happeningsId)!;

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
        Battlefield {BATTLEFIELD.min} to {BATTLEFIELD.max}. {BATTLEFIELD.guidance} Games last five rounds.
      </p>

      <RollTable
        title="Objectives"
        note="Rolled once terrain has been placed, before armies are deployed."
        table={OBJECTIVES_TABLE}
      />

      <RollTable
        title="Unusual Properties"
        note="Only if a strategic landmark was placed. The controlling unit keeps the property until the end of the next turn."
        table={UNUSUAL_PROPERTIES_TABLE}
      />

      {/* Deployment maps */}
      <div className="rounded border p-3 mb-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <h4 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Deployment map</h4>
          <button
            onClick={() => { const r = rollD6(); setMapId(DEPLOYMENT_MAPS[r - 1].id); setMirrored(false); }}
            className="px-3 py-1 rounded text-sm font-semibold"
            style={{ backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', color: 'var(--color-accent-amber)' }}
          >⚄ Roll</button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {DEPLOYMENT_MAPS.map((m) => (
            <button
              key={m.id}
              onClick={() => { setMapId(m.id); setMirrored(false); }}
              className="px-2 py-1 rounded text-xs"
              style={{
                border: '1px solid var(--color-border)',
                backgroundColor: mapId === m.id ? 'rgba(217,119,6,0.12)' : 'var(--color-bg)',
                color: mapId === m.id ? 'var(--color-accent-amber)' : 'var(--color-text-secondary)',
              }}
            >{m.roll[0]}. {m.name}</button>
          ))}
        </div>
        {selectedMap ? (
          <>
            <BattleMarchMap id={selectedMap.id} mirrored={mirrored} />
            <p className="text-sm mt-2" style={{ color: 'var(--color-accent-amber)' }}>{selectedMap.name}</p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{selectedMap.effect}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{selectedMap.dimensions}</p>
            {selectedMap.mirrorable && (
              <button
                onClick={() => setMirrored((v) => !v)}
                className="px-2 py-1 rounded text-xs mt-2"
                style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg)', color: 'var(--color-text-secondary)' }}
              >⇄ Mirror left/right{mirrored ? ' (on)' : ''}</button>
            )}
          </>
        ) : (
          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Roll or choose a map to see its deployment zones.</p>
        )}
        <p className="text-xs mt-3" style={{ color: 'var(--color-text-secondary)' }}>{DEPLOYMENT_RULES.order}</p>
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{DEPLOYMENT_RULES.mirroring}</p>
      </div>

      {/* Random Happenings */}
      <div className="rounded border p-3 mb-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
        <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Random Happenings</h4>
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-secondary)' }}>{RANDOM_HAPPENINGS_USAGE}</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {RANDOM_HAPPENINGS.map((t) => (
            <button
              key={t.id}
              onClick={() => setHappeningsId(t.id)}
              className="px-2 py-1 rounded text-xs"
              style={{
                border: '1px solid var(--color-border)',
                backgroundColor: happeningsId === t.id ? 'rgba(217,119,6,0.12)' : 'var(--color-bg)',
                color: happeningsId === t.id ? 'var(--color-accent-amber)' : 'var(--color-text-secondary)',
              }}
            >{t.name}</button>
          ))}
        </div>
        <RollTable title={happenings.name} note={happenings.when} table={happenings.results} />
      </div>

      {/* Secondary objectives */}
      <div className="rounded border p-3 mb-4" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
        <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Secondary objectives</h4>
        {SECONDARY_OBJECTIVES.map((o) => (
          <div key={o.id} className="mb-2">
            <p className="text-sm" style={{ color: 'var(--color-accent-amber)' }}>{o.name}</p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{o.rules}</p>
            <p className="text-xs italic" style={{ color: 'var(--color-text-secondary)' }}>{o.victoryPoints}</p>
          </div>
        ))}
      </div>

      {/* Victory points */}
      <div className="rounded border p-3" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}>
        <h4 className="text-sm font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>Victory Points</h4>
        <ul className="text-xs space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
          {VICTORY_POINTS.map((v) => (
            <li key={v.name}>
              <strong style={{ color: 'var(--color-accent-amber)' }}>{v.vp}</strong> {v.name}
              {v.perTurn && <em> (each turn)</em>} — {v.detail}
            </li>
          ))}
        </ul>
      </div>

      <p className="text-xs mt-3" style={{ color: 'var(--color-text-secondary)' }}>
        {OBJECTIVE_RULES.control}
      </p>
    </div>
  );
}
